import React, {useState, useEffect, useRef, useCallback} from "react";
import {useForm} from "react-hook-form";
import {
    CheckCircle, X, Edit, Trash2, Plus, Copy, ArrowUpDown,
    ArrowUp, ArrowDown, Upload, Download, RefreshCw, GitMerge,
    Package, Barcode, Check, ChevronLeft, Calendar, Clock
} from "lucide-react";
import {useParams, useNavigate, useLocation} from "react-router-dom";
import ColorPalette from "../ColorPalette";
import IOSSwitch from "../../Common/IOSSwitch";
import {MdClose} from 'react-icons/md';
import secureStorage from "../../../utils/secureStorage";
import {api} from "../../../config/api";
import {Tab} from '@headlessui/react';
import BulkVariantCreator from "./BulkVariantCreator";
import ReferencePickerModal from "../../Common/ReferencePickerModal";
import {categoriesConfig, brandsConfig, taxesConfig, unitsConfig, productsConfig} from "../../Common/referenceConfigs";
import {generateProductBarcode} from "../../../utils/barcodeGenerator";
import ProductOptionGroups from "./ProductOptionGroups";
import usePersistedTab from "../../../hooks/usePersistedTab";
import PurchasePriceHistoryModal from "./PurchasePriceHistoryModal";
import {tabClass} from '../../Common/TabBar';
import Button from '../../Common/Button';
import ActionMenu from '../../Common/ActionMenu';
import {HeaderAction, SearchInput, ToolbarSelect, ListCard, ListEmpty, thClass, mobileListClass, mobileRowClass, Avatar} from '../../../stock/StockUI';
import Notification from "../../Common/Notification";

// Order of the Tab.Group panels — used to translate the persisted tab name to/from its numeric index.
const EDIT_PRODUCT_TAB_KEYS = ["produit", "declinaisons", "options"];

// Sortable column header for the déclinaisons table — same as ClientsTable's SortableHeader
const VariantSortableHeader = ({field, sortConfig, onSort, children}) => {
    const isActive = sortConfig.key === field;
    return (
        <th
            className={`group ${thClass} cursor-pointer select-none hover:text-gray-900 dark:hover:text-gray-100 transition-colors`}
            onClick={() => onSort(field)}
        >
            <div className="flex items-center gap-1.5">
                <span>{children}</span>
                {isActive ? (
                    sortConfig.direction === 'asc'
                        ? <ArrowUp className="w-3.5 h-3.5 text-primary"/>
                        : <ArrowDown className="w-3.5 h-3.5 text-primary"/>
                ) : (
                    <ArrowUpDown className="w-3.5 h-3.5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"/>
                )}
            </div>
        </th>
    );
};

const formatVariantPrice = (price) => price ? `${Number(price).toFixed(2)} DH` : '—';

// Variant image, or the shared coloured Avatar with its initials
const VariantThumb = ({variant, fallbackColor, size}) => variant.imageUrl ? (
    <img
        src={variant.thumbnailUrl || variant.imageUrl}
        alt={variant.name}
        className={`${size === 'lg' ? 'h-16 w-16' : 'h-12 w-12'} flex-shrink-0 rounded-lg object-cover shadow-sm`}
    />
) : (
    <Avatar name={variant.name} color={variant.color || fallbackColor} size={size}/>
);

const VariantStatus = ({active}) => (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        active
            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
    }`}>
        {active ? 'Actif' : 'Inactif'}
    </span>
);

// Attributes come either as [{name, value}] or as a {name: value} object
const VariantAttributes = ({variant, empty = null}) => {
    const attrs = Array.isArray(variant.attributes)
        ? variant.attributes.map(a => [a.name, a.value])
        : variant.attributes && typeof variant.attributes === 'object'
            ? Object.entries(variant.attributes)
            : [];
    if (attrs.length === 0) return empty;
    return (
        <div className="flex flex-wrap gap-1">
            {attrs.map(([name, value], idx) => (
                <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                    {name}: {value}
                </span>
            ))}
        </div>
    );
};

// Variant row actions: one "⋯" menu for desktop rows and mobile cards
const VariantActions = ({variant, onEdit, onDuplicate, onDelete}) => (
    <ActionMenu
        label={`Actions pour ${variant.name || 'la déclinaison'}`}
        items={[
            {label: 'Modifier', icon: Edit, onClick: () => onEdit(variant.id)},
            {label: 'Dupliquer', icon: Copy, onClick: () => onDuplicate(variant.id)},
            {divider: true},
            {label: 'Supprimer', icon: Trash2, danger: true, onClick: () => onDelete(variant.id)},
        ]}
    />
);

const EditProductForm = () => {
    const {productId} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    // Where "Retour" leads: the catalogue list we came from (filters + page kept), handed
    // along through the variant pages. Not history back: coming back from a variant page
    // pushes this page again, so navigate(-1) would bounce back to the variant.
    const returnTo = location.state?.returnTo || "/catalogue";
    const goBack = () => navigate(returnTo);
    const [dataLoaded, setDataLoaded] = useState(false);
    const productLoadedRef = useRef(false); // Track if product has been loaded

    const [currentColor, setCurrentColor] = useState("#4DB6AC");
    const [showPalette, setShowPalette] = useState(false);
    const [productImage, setProductImage] = useState(null);
    const [isActive, setIsActive] = useState(true);
    const [isStockable, setIsStockable] = useState(true);
    const [isVendable, setIsVendable] = useState(true);
    const [isIngredient, setIsIngredient] = useState(false);
    // Calendar / online booking (same settings as the product's "Paramètres calendrier")
    // salons de beauté only (busniessType === "001")
    const [isBeautySalon, setIsBeautySalon] = useState(false);
    const [showOnCalendar, setShowOnCalendar] = useState(false);
    const [duration, setDuration] = useState(30);
    const [internalCode, setInternalCode] = useState('');
    const [purchasePriceHt, setPurchasePriceHt] = useState('');
    const [purchasePriceTtc, setPurchasePriceTtc] = useState('');
    const [showPriceHistory, setShowPriceHistory] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [units, setUnits] = useState([]);
    const [taxRates, setTaxRates] = useState([]);
    const [variants, setVariants] = useState([]);
    const [selectedVariants, setSelectedVariants] = useState([]);
    const [selectedAllVariants, setSelectedAllVariants] = useState(false);
    // Remembers the last tab used for THIS product, so navigating away to edit a
    // variant (a separate route) and back restores it instead of resetting to "Produit".
    const [activeTabKey, setActiveTabKey] = usePersistedTab(`tab_edit_product_${productId}`, EDIT_PRODUCT_TAB_KEYS[0]);
    const activeTab = Math.max(0, EDIT_PRODUCT_TAB_KEYS.indexOf(activeTabKey));
    const handleTabChange = (index) => setActiveTabKey(EDIT_PRODUCT_TAB_KEYS[index] || EDIT_PRODUCT_TAB_KEYS[0]);
    // New state for enhanced variant management
    const [searchQuery, setSearchQuery] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [filterActive, setFilterActive] = useState('all'); // 'all', 'active', 'inactive'
    const [showImportExport, setShowImportExport] = useState(false);
    // Selected references (full objects for display)
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [selectedTax, setSelectedTax] = useState(null);
    const [activePicker, setActivePicker] = useState(null); // 'category' | 'brand' | 'unit' | 'tax' | null
    const [notification, setNotification] = useState({
        show: false,
        type: 'success',
        message: '',
    });
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [variantsToDelete, setVariantsToDelete] = useState([]);
    const [showBulkCreator, setShowBulkCreator] = useState(false);
    // Restaurant accounts (busniessType === "004") see "Options" (dish customization);
    // all other commerce types see the same feature labeled "Packs".
    const [isRestaurant, setIsRestaurant] = useState(false);
    // Convert-to-variant flow
    const [showConvertPicker, setShowConvertPicker] = useState(false);
    const [convertTarget, setConvertTarget] = useState(null);
    const [isConverting, setIsConverting] = useState(false);

    const {
        register,
        handleSubmit,
        formState: {errors, isSubmitting},
        watch,
        setValue
    } = useForm();

    const formRef = useRef(null);
    const selectionBarRef = useRef(null);

    // Function to show a notification
    const showNotification = (type, message) => {
        setNotification({
            show: true,
            type,
            message,
        });

        if (type === 'success') {
            setTimeout(() => {
                setNotification(prev => ({...prev, show: false}));
            }, 5000);
        }
    };

    // Function to hide notification
    const hideNotification = () => {
        setNotification(prev => ({...prev, show: false}));
    };


    // Apply animation when selection changes
    useEffect(() => {
        if (selectedVariants.length > 0 && selectionBarRef.current) {
            selectionBarRef.current.classList.remove('selection-bar-appear');
            void selectionBarRef.current.offsetWidth;
            selectionBarRef.current.classList.add('selection-bar-appear');
        }
    }, [selectedVariants.length]);

    // Define fetchProduct as a regular function so it can be reused
    const fetchProduct = useCallback(async () => {
        try {
            setIsLoading(true);
            const accountId = await secureStorage.getItem("selectedAccount");

            const response = await api.get(`/products/${productId}`, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json'
                }
            });

            const product = response.data;

            setValue("name", product.name);
            setValue("description", product.description || "");
            setValue("price", product.price);
            setValue("barcode", product.barcode || "");
            setValue("minStock", product.minStock || 0);

            if (product.categoryId) {
                setValue("categoryId", product.categoryId);
            } else if (product.category && product.category.id) {
                setValue("categoryId", product.category.id);
            }
            // Store full category object if available
            if (product.category) {
                setSelectedCategory(product.category);
            }

            if (product.brandId) {
                setValue("brandId", product.brandId);
            } else if (product.brand && product.brand.id) {
                setValue("brandId", product.brand.id);
            }
            // Store full brand object if available
            if (product.brand) {
                setSelectedBrand(product.brand);
            }

            if (product.unitsId) {
                setValue("unitsId", product.unitsId);
            } else if (product.units && product.units.id) {
                setValue("unitsId", product.units.id);
            }
            // Store full unit object if available
            if (product.units) {
                setSelectedUnit(product.units);
            }

            if (product.taxesId) {
                setValue("taxId", product.taxesId);
            } else if (product.taxes && product.taxes.id) {
                setValue("taxId", product.taxes.id);
            }
            // Store full tax object if available
            if (product.taxes) {
                setSelectedTax(product.taxes);
            }

            setCurrentColor(product.color || "#4DB6AC");
            setIsActive(product.active === true);
            setIsStockable(product.isStockable === true);
            setIsVendable(product.isVendable === true);
            setIsIngredient(product.isIngredient === true);
            setShowOnCalendar(product.showOnCalendar === true);
            setDuration(product.duration || 30);
            setInternalCode(product.internalCode || '');
            setPurchasePriceHt(product.purchasePriceHt != null ? String(product.purchasePriceHt) : '');
            setPurchasePriceTtc(product.purchasePriceTtc != null ? String(product.purchasePriceTtc) : '');

            if (product.imageUrl) {
                setProductImage(product.imageUrl);
            }

            if (product.variants && Array.isArray(product.variants)) {
                setVariants(product.variants);
            }

            productLoadedRef.current = true; // Mark as loaded
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching product:", error);
            showNotification('error', "Erreur lors du chargement du produit");
            setIsLoading(false);
        }
    }, [productId, setValue]);

    useEffect(() => {
        const fetchReferenceData = async () => {
            try {
                const accountId = await secureStorage.getItem("selectedAccount");
                setIsRestaurant(accountId?.busniessType === "004");
                setIsBeautySalon(accountId?.busniessType === "001");
                const headers = {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json'
                };

                const [categoriesRes, brandsRes, unitsRes, taxRatesRes] = await Promise.all([
                    api.get('/categories', {headers, params: {itemsPerPage: 100}}),
                    api.get('/brands', {headers, params: {itemsPerPage: 100}}),
                    api.get('/units', {headers, params: {itemsPerPage: 100}}),
                    api.get('/taxes', {headers, params: {itemsPerPage: 100}})
                ]);

                setCategories(categoriesRes.data['hydra:member'] || categoriesRes.data.data || categoriesRes.data || []);
                setBrands(brandsRes.data['hydra:member'] || brandsRes.data.data || brandsRes.data || []);
                setUnits(unitsRes.data['hydra:member'] || unitsRes.data.data || unitsRes.data || []);
                setTaxRates(taxRatesRes.data.items || []);
                setDataLoaded(true); // ✅ Mark data as loaded
            } catch (error) {
                console.error("Error fetching reference data:", error);
                showNotification('error', "Erreur lors du chargement des données de référence");
            }
        };

        fetchReferenceData();
    }, []); // ✅ Only run once on mount

    // Fetch product data when reference data is loaded
    useEffect(() => {
        if (!dataLoaded || productLoadedRef.current) return; // Check if already loaded

        if (productId && dataLoaded) {
            fetchProduct();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId, dataLoaded]); // setValue intentionally excluded to prevent infinite loop

    // ✅ Only depend on productId and dataLoaded flag
    const onSubmit = async (data) => {
        try {
            showNotification('loading', "Mise à jour du produit...");

            // Create FormData object
            const formData = new FormData();

            // Add the ID to the form data
            formData.append("id", productId);

            // Add necessary fields with proper values
            formData.append("name", data.name);
            formData.append("color", currentColor);
            formData.append("description", data.description || "");
            // Price and barcode inputs aren't rendered once the product has variants
            // (they're managed per déclinaison) — omit them so the backend leaves
            // the existing values untouched instead of overwriting with "undefined".
            if (variants.length === 0) {
                formData.append("price", data.price);
                formData.append("barcode", data.barcode || "");
                // minStock input is also unregistered once hidden — omit it too so the
                // backend keeps the existing value instead of resetting it to 0.
                formData.append("minStock", data.minStock || 0);
            }
            formData.append("category", `/api/categories/${selectedCategory?.id || data.categoryId}`);
            if (selectedBrand?.id || data.brandId) formData.append("brand", `/api/brands/${selectedBrand?.id || data.brandId}`);
            formData.append("units", `/api/units/${selectedUnit?.id || data.unitsId}`);
            if (selectedTax?.id || data.taxId) formData.append("taxes", `/api/taxes/${selectedTax?.id || data.taxId}`);
            formData.append("active", String(isActive));
            formData.append("isStockable", String(isStockable));
            formData.append("isVendable", String(isVendable));
            formData.append("isIngredient", String(isIngredient));
            if (isBeautySalon) {
                formData.append("show_on_calendar", String(showOnCalendar));
                formData.append("duration", String(duration));
            }
            formData.append("internalCode", internalCode || "");
            formData.append("purchasePriceHt", purchasePriceHt);
            formData.append("purchasePriceTtc", purchasePriceTtc);

            // Handle image upload - if there's a new image
            if (productImage instanceof File) {
                formData.append("image", productImage);
            } else if (typeof productImage === 'string' && productImage.startsWith('data:')) {
                // Convert base64 to file
                const response = await fetch(productImage);
                const blob = await response.blob();
                const file = new File([blob], "product-image.jpg", {type: "image/jpeg"});
                formData.append("image", file);
            }
            // If image is a URL from the server, we don't need to send it back

            // Get account ID
            const accountId = await secureStorage.getItem("selectedAccount");

            // Log the form data for debugging
            for (let pair of formData.entries()) {
                console.log(pair[0] + ': ' + pair[1]);
            }

            // Use POST method with /update endpoint
            const response = await api.post(`/products/${productId}/update`, formData, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json',
                    'Content-Type': 'multipart/form-data'
                }
            });

            console.log("Product updated:", response.data);
            showNotification('success', "Produit mis à jour avec succès");

            // Give time for notification to be seen, then return to the list (preserves pagination)
            setTimeout(goBack, 1500);
        } catch (error) {
            console.error("Error updating product:", error);
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                error.message ||
                "Une erreur s'est produite lors de la mise à jour";
            showNotification('error', `Erreur: ${errorMessage}`);
        }
    };

    // Variants table handlers
    const handleSelectAllVariants = () => {
        if (selectedAllVariants) {
            setSelectedVariants([]);
        } else {
            setSelectedVariants(variants.map(variant => variant.id));
        }
        setSelectedAllVariants(!selectedAllVariants);
    };

    const handleSelectVariant = (id, e) => {
        e.stopPropagation();
        setSelectedVariants((prevSelected) =>
            prevSelected.includes(id)
                ? prevSelected.filter(itemId => itemId !== id)
                : [...prevSelected, id]
        );
    };

    const handleDeleteVariant = (variantId, e) => {
        e?.stopPropagation();
        setVariantsToDelete([variantId]);
        setShowConfirmDialog(true);
    };

    const handleDeleteSelectedVariants = () => {
        if (selectedVariants.length === 0) return;
        setVariantsToDelete(selectedVariants);
        setShowConfirmDialog(true);
    };

    const confirmDelete = async () => {
        try {
            showNotification('loading', `Suppression de ${variantsToDelete.length > 1 ? 'déclinaisons' : 'la déclinaison'}...`);
            const accountId = await secureStorage.getItem("selectedAccount");

            await Promise.all(
                variantsToDelete.map(id =>
                    api.delete(`/variants/${id}`, {
                        headers: {
                            'X-account-id': accountId.id,
                            'Accept': 'application/json'
                        }
                    })
                )
            );

            setVariants(prevVariants =>
                prevVariants.filter(variant => !variantsToDelete.includes(variant.id))
            );

            setSelectedVariants(prev => prev.filter(id => !variantsToDelete.includes(id)));
            if (selectedVariants.length === 0) {
                setSelectedAllVariants(false);
            }

            showNotification('success', `${variantsToDelete.length > 1 ? 'Déclinaisons supprimées' : 'Déclinaison supprimée'} avec succès`);
            setShowConfirmDialog(false);
            setVariantsToDelete([]);
        } catch (error) {
            showNotification('error', "Erreur lors de la suppression des déclinaisons");
            setShowConfirmDialog(false);
        }
    };

    const cancelDelete = () => {
        setShowConfirmDialog(false);
        setVariantsToDelete([]);
    };

    // Convert-to-variant handlers
    const handleSelectConvertTarget = (item) => {
        if (String(item.id) === String(productId)) {
            showNotification('error', "Un produit ne peut pas devenir une déclinaison de lui-même");
            return;
        }
        setShowConvertPicker(false);
        setConvertTarget(item);
    };

    const cancelConvert = () => {
        setConvertTarget(null);
    };

    const confirmConvert = async () => {
        if (!convertTarget) return;
        try {
            setIsConverting(true);
            showNotification('loading', "Conversion en déclinaison...");
            const accountId = await secureStorage.getItem("selectedAccount");

            await api.post(`/products/${productId}/convert-to-variant`,
                {targetProductId: convertTarget.id},
                {
                    headers: {
                        'X-account-id': accountId.id,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                }
            );

            showNotification('success', `Produit converti en déclinaison de "${convertTarget.name}"`);
            setConvertTarget(null);

            setTimeout(() => {
                navigate(`/catalogue/product/edit/${convertTarget.id}`, {replace: true, state: {returnTo}});
            }, 1200);
        } catch (error) {
            console.error("Error converting product to variant:", error);
            const errorMessage = error.response?.data?.error ||
                error.response?.data?.detail ||
                error.response?.data?.message ||
                error.message ||
                "Erreur lors de la conversion";
            showNotification('error', `Erreur: ${errorMessage}`);
        } finally {
            setIsConverting(false);
        }
    };

    const handleEditVariant = (variantId) => {
        navigate(`/catalogue/products/edit/${productId}/variants/${variantId}`, {state: {returnTo}});
    };

    const handleAddVariant = () => {
        navigate(`/catalogue/products/${productId}/variants/new`, {state: {returnTo}});
    };

    const handleDuplicateVariant = async (variantId, e) => {
        e?.stopPropagation();
        try {
            showNotification('loading', "Duplication de la déclinaison en cours...");
            const accountId = await secureStorage.getItem("selectedAccount");

            // Get the variant to duplicate
            const variantRes = await api.get(`/variants/${variantId}`, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json'
                }
            });

            const variant = variantRes.data;

            // Create FormData for the new variant
            const formData = new FormData();
            formData.append("name", `${variant.name} (Copie)`);
            formData.append("color", variant.color || currentColor);
            formData.append("description", variant.description || "");
            formData.append("price", variant.price);
            formData.append("barcode", ""); // Clear barcode for duplicate
            formData.append("product", `/api/products/${productId}`);
            formData.append("units", `/api/units/${variant.units?.id || variant.unitsId}`);
            formData.append("taxe", `/api/taxes/${variant.taxe?.id || variant.taxeId}`);  // field is `taxe` (singular) on ProductVariant
            formData.append("active", variant.active);
            formData.append("isStockable", variant.isStockable);
            formData.append("isVendable", variant.isVendable);
            formData.append("show_on_calendar", String(variant.showOnCalendar === true));
            formData.append("duration", String(variant.duration || 30));

            // Create the duplicate
            const response = await api.post('/variants', formData, {
                headers: {
                    'X-account-id': accountId.id,
                    'Content-Type': 'multipart/form-data',
                    'Accept': 'application/json'
                }
            });

            // Add the new variant to the list
            setVariants(prevVariants => [...prevVariants, response.data]);
            showNotification('success', "Déclinaison dupliquée avec succès");
        } catch (error) {
            console.error("Error duplicating variant:", error);
            showNotification('error', "Erreur lors de la duplication de la déclinaison");
        }
    };

    // Sort variants
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Filter and search variants
    const getFilteredAndSortedVariants = () => {
        let filtered = [...variants];

        // Apply search filter
        if (searchQuery) {
            filtered = filtered.filter(variant =>
                variant.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                variant.barcode?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                variant.price?.toString().includes(searchQuery)
            );
        }

        // Apply status filter
        if (filterActive !== 'all') {
            filtered = filtered.filter(variant =>
                filterActive === 'active' ? variant.active : !variant.active
            );
        }

        // Apply sorting
        if (sortConfig.key) {
            filtered.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                // Handle null/undefined values
                if (aVal === null || aVal === undefined) return 1;
                if (bVal === null || bVal === undefined) return -1;

                // Convert to lowercase for string comparison
                if (typeof aVal === 'string') aVal = aVal.toLowerCase();
                if (typeof bVal === 'string') bVal = bVal.toLowerCase();

                if (aVal < bVal) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aVal > bVal) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        return filtered;
    };

    // Export variants to CSV
    const handleExportVariants = () => {
        try {
            const csvData = [
                ['Nom', 'Prix', 'Code-barres', 'Stock', 'Statut'],
                ...variants.map(v => [
                    v.name || '',
                    v.price || '',
                    v.barcode || '',
                    v.stock !== undefined ? v.stock : '',
                    v.active ? 'Actif' : 'Inactif'
                ])
            ];

            const csvContent = csvData.map(row => row.join(',')).join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            link.setAttribute('href', url);
            link.setAttribute('download', `declinaisons_${productId}_${new Date().getTime()}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            showNotification('success', 'Déclinaisons exportées avec succès');
        } catch (error) {
            showNotification('error', 'Erreur lors de l\'export des déclinaisons');
        }
    };

    const handleColorChange = (color) => {
        setCurrentColor(color);
        setShowPalette(false);
    };

    const handleImageChange = (image) => {
        setProductImage(image);
    };

    const togglePalette = () => {
        setShowPalette((prev) => !prev);
    };

    const toggleActive = () => {
        setIsActive((prev) => !prev);
    };

    const toggleStockable = () => {
        setIsStockable((prev) => !prev);
    };

    const toggleVendable = () => {
        setIsVendable((prev) => !prev);
    };

    const toggleIngredient = () => {
        setIsIngredient((prev) => !prev);
    };

    const handleDeleteImage = () => {
        setProductImage(null);
    };

    // Prix d'achat HT/TTC — dérivation automatique via le taux de TVA sélectionné
    // (ex: 80 DH TTC à 20% → 66,67 DH HT). Si aucune taxe n'est sélectionnée,
    // les deux champs restent en saisie libre indépendante.
    const purchaseTaxRate = selectedTax ? parseFloat(selectedTax.value) : null;

    const handlePurchaseHtChange = (value) => {
        setPurchasePriceHt(value);
        if (purchaseTaxRate !== null && !isNaN(purchaseTaxRate) && value !== '') {
            const ht = parseFloat(value);
            if (!isNaN(ht)) setPurchasePriceTtc((ht * (1 + purchaseTaxRate / 100)).toFixed(2));
        }
    };

    const handlePurchaseTtcChange = (value) => {
        setPurchasePriceTtc(value);
        if (purchaseTaxRate !== null && !isNaN(purchaseTaxRate) && value !== '') {
            const ttc = parseFloat(value);
            if (!isNaN(ttc)) setPurchasePriceHt((ttc / (1 + purchaseTaxRate / 100)).toFixed(2));
        }
    };

    /* ─── Reference Selector Button component ─── */
    const RefSelector = ({label, required, value, placeholder, onClick, onClear}) => (
        <div>
            <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
            </div>
            <button
                type="button"
                onClick={onClick}
                className={`w-full flex items-center justify-between px-3 sm:px-4 py-2.5 text-sm border rounded-xl transition-all text-left ${
                    value
                        ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                        : 'border-dashed border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/50 hover:border-primary/50'
                }`}
            >
                {value ? (
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        {value.color && !value.hideColor && (
                            <span className="w-5 h-5 rounded-md flex-shrink-0" style={{backgroundColor: value.color}}/>
                        )}
                        <span className="text-gray-800 dark:text-white font-medium truncate">
                            {value.displayLabel || value.name || '—'}
                        </span>
                    </div>
                ) : (
                    <span className="text-gray-400 dark:text-gray-500">{placeholder}</span>
                )}
                <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                    {value && (
                        <span onClick={(e) => { e.stopPropagation(); onClear(); }}
                              className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer">
                            <X className="w-3.5 h-3.5"/>
                        </span>
                    )}
                    <Plus className="w-4 h-4 text-gray-400"/>
                </div>
            </button>
        </div>
    );

    if (isLoading) {
        return (
            <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div
                        className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-primary border-r-transparent align-[-0.125em]"
                        role="status">
                        <span
                            className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">Loading...</span>
                    </div>
                    <p className="mt-2 text-gray-600 dark:text-gray-300">Chargement du produit...</p>
                </div>
            </div>
        );
    }

    const filteredVariants = getFilteredAndSortedVariants();

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900">
            {/* Sticky header: back · title · actions (main action on the right, filled) */}
            <header className="sticky top-0 z-30 flex items-center gap-2 sm:gap-4 px-3 sm:px-6 h-14 sm:h-16 border-b border-gray-200 dark:border-gray-700 bg-white/85 dark:bg-gray-900/85 backdrop-blur-xl">
                <div className="flex-1 min-w-0">
                    <Button variant="ghost" icon={ChevronLeft} compact onClick={goBack} className="-ml-2">
                        Retour
                    </Button>
                </div>
                <h1 className="text-base sm:text-lg font-semibold text-center text-gray-800 dark:text-white truncate">
                    {productId ? "Modifier le produit" : "Nouveau produit"}
                </h1>
                <div className="flex-1 min-w-0 flex items-center justify-end gap-1 sm:gap-2">
                    {productId && (
                        <ActionMenu
                            label="Plus d'actions"
                            items={[{
                                label: variants.length > 0 ? 'Convertir (a déjà des déclinaisons)' : 'Convertir en déclinaison',
                                icon: GitMerge,
                                disabled: variants.length > 0,
                                onClick: () => setShowConvertPicker(true),
                            }]}
                            trigger={{variant: 'plain'}}
                        />
                    )}
                    <Button variant="plain" icon={Check} loading={isSubmitting}
                            onClick={() => formRef.current && handleSubmit(onSubmit)()}>
                        Enregistrer
                    </Button>
                </div>
            </header>

            <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
                <Tab.Group selectedIndex={activeTab} onChange={handleTabChange}>
                    {/* Underlined tabs — same look as the Activité tabs */}
                    <Tab.List className="flex overflow-x-auto no-scrollbar w-full md:justify-center border-b border-gray-200 dark:border-gray-700 mb-6 sm:mb-8">
                        <Tab className={({selected}) => `${tabClass(selected)} focus:outline-none`}>
                            Produit
                        </Tab>
                        <Tab className={({selected}) => `${tabClass(selected)} focus:outline-none`}>
                            {({selected}) => (
                                <>
                                    <span>Déclinaisons</span>
                                    {variants.length > 0 && (
                                        <span className={`px-1.5 sm:px-2 py-0.5 text-xs rounded-full ${
                                            selected ? 'bg-primary/10 text-primary' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                                        }`}>
                                            {variants.length}
                                        </span>
                                    )}
                                </>
                            )}
                        </Tab>
                        <Tab className={({selected}) => `${tabClass(selected)} focus:outline-none`}>
                            {isRestaurant ? "Options" : "Packs"}
                        </Tab>
                    </Tab.List>

                    <Tab.Panels>
                        {/* Main Product Form Panel */}
                        <Tab.Panel>
                            <form
                                ref={formRef}
                                className="space-y-4 sm:space-y-6 w-full"
                                onSubmit={handleSubmit(onSubmit)}
                            >
                                {/* Image/Color section - responsive */}
                                <div className="flex flex-col items-center space-y-3">
                                    <div className="text-center mb-2">
                                        {productImage ? (
                                            <div className="relative w-24 h-24 sm:w-32 sm:h-32 mx-auto">
                                                <img
                                                    src={productImage}
                                                    alt="Product"
                                                    className="w-24 h-24 sm:w-32 sm:h-32 object-cover rounded-lg shadow-sm cursor-pointer"
                                                    onClick={togglePalette}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={handleDeleteImage}
                                                    className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 bg-red-500 text-white rounded-full p-1 sm:p-1.5 shadow-sm hover:bg-red-600 transition-colors"
                                                    aria-label="Delete image"
                                                >
                                                    <MdClose className="w-3 h-3 sm:w-4 sm:h-4"/>
                                                </button>
                                            </div>
                                        ) : (
                                            <div
                                                className="w-24 h-24 sm:w-32 sm:h-32 rounded-lg flex items-center justify-center cursor-pointer shadow-sm hover:shadow-md transition-shadow mx-auto text-white text-xs sm:text-sm"
                                                onClick={togglePalette}
                                                style={{backgroundColor: currentColor}}
                                            >
                                                Éditer
                                            </div>
                                        )}
                                        <span className="text-xs text-gray-500 dark:text-gray-400 mt-2 block">
                                            Cliquez pour modifier
                                        </span>
                                    </div>
                                </div>

                                {/* Form fields - responsive grid */}
                                <div className="space-y-4 sm:space-y-6">
                                    <div>
                                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Nom du produit <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="name"
                                            type="text"
                                            {...register("name", {required: "Le nom est obligatoire"})}
                                            className={`w-full p-2 sm:p-3 text-sm sm:text-base border rounded-md focus:ring-primary focus:border-primary
                                                ${errors.name ? "border-red-500 focus:ring-red-500 focus:border-red-500" : "border-gray-300 dark:border-gray-700"}
                                                bg-white dark:bg-gray-800 text-gray-900 dark:text-white`}
                                        />
                                        {errors.name && (
                                            <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.name.message}</p>
                                        )}
                                    </div>

                                    {/* Description field */}
                                    <div>
                                        <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Description
                                        </label>
                                        <textarea
                                            id="description"
                                            {...register("description")}
                                            rows={3}
                                            className="w-full p-2 sm:p-3 text-sm sm:text-base border border-gray-300 dark:border-gray-700 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                        />
                                    </div>

                                    {/* Price and barcode are set per déclinaison once the product has variants */}
                                    {variants.length > 0 ? (
                                        <div className="p-3 sm:p-4 rounded-md bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                                            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                                Ce produit a des déclinaisons : le prix, le code-barres, le stock minimum, l'unité et le taux de TVA se gèrent individuellement sur chaque déclinaison.
                                            </p>
                                        </div>
                                    ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                        <div>
                                            <label htmlFor="price" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Prix <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                id="price"
                                                type="number"
                                                step="0.01"
                                                {...register("price", {
                                                    required: "Le prix est obligatoire",
                                                    min: {value: 0, message: "Le prix doit être positif"}
                                                })}
                                                className={`w-full p-2 sm:p-3 text-sm sm:text-base border rounded-md focus:ring-primary focus:border-primary
                                                    ${errors.price ? "border-red-500 focus:ring-red-500 focus:border-red-500" : "border-gray-300 dark:border-gray-700"}
                                                    bg-white dark:bg-gray-800 text-gray-900 dark:text-white`}
                                            />
                                            {errors.price && (
                                                <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.price.message}</p>
                                            )}
                                        </div>
                                        <div>
                                            <label htmlFor="barcode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Code-barres
                                            </label>
                                            <div className="flex gap-2">
                                                <input
                                                    id="barcode"
                                                    type="text"
                                                    {...register("barcode")}
                                                    className="flex-1 min-w-0 p-2 sm:p-3 text-sm sm:text-base border border-gray-300 dark:border-gray-700 rounded-md focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={async () => {
                                                        try {
                                                            const barcode = await generateProductBarcode();
                                                            setValue("barcode", barcode, {shouldDirty: true});
                                                        } catch (e) {
                                                            console.error("Erreur lors de la génération du code-barres", e);
                                                        }
                                                    }}
                                                    title="Générer un code-barres"
                                                    className="flex items-center gap-1.5 px-3 py-2 sm:py-3 text-sm font-medium text-primary border border-primary rounded-md hover:bg-primary hover:text-white transition-colors whitespace-nowrap"
                                                >
                                                    <RefreshCw size={16}/>
                                                    <span className="hidden sm:inline">Générer</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    )}

                                    {/* Stock minimum field — variant-level once the product has déclinaisons */}
                                    {variants.length === 0 && (
                                    <div>
                                        <label htmlFor="minStock" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Stock minimum
                                        </label>
                                        <input
                                            id="minStock"
                                            type="number"
                                            {...register("minStock", {
                                                min: {value: 0, message: "Le stock minimum doit être positif ou nul"}
                                            })}
                                            placeholder="Stock minimum"
                                            className={`w-full p-2 sm:p-3 text-sm sm:text-base border rounded-md focus:ring-primary focus:border-primary
                                                ${errors.minStock ? "border-red-500 focus:ring-red-500 focus:border-red-500" : "border-gray-300 dark:border-gray-700"}
                                                bg-white dark:bg-gray-800 text-gray-900 dark:text-white`}
                                        />
                                        {errors.minStock && (
                                            <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.minStock.message}</p>
                                        )}
                                    </div>
                                    )}

                                    {/* Responsive grid for category and brand */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                        <RefSelector
                                            label="Catégorie"
                                            required
                                            value={selectedCategory}
                                            placeholder="Sélectionner une catégorie"
                                            onClick={() => setActivePicker('category')}
                                            onClear={() => setSelectedCategory(null)}
                                        />
                                        <RefSelector
                                            label="Marque"
                                            value={selectedBrand}
                                            placeholder="Sélectionner une marque"
                                            onClick={() => setActivePicker('brand')}
                                            onClear={() => setSelectedBrand(null)}
                                        />
                                    </div>

                                    {/* Responsive grid for units and tax — variant-level once the product has déclinaisons */}
                                    {variants.length === 0 && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                        <RefSelector
                                            label="Unité"
                                            required
                                            value={selectedUnit ? {...selectedUnit, displayLabel: `${selectedUnit.name} (${selectedUnit.code || ''})`, hideColor: true} : null}
                                            placeholder="Sélectionner une unité"
                                            onClick={() => setActivePicker('unit')}
                                            onClear={() => setSelectedUnit(null)}
                                        />
                                        <RefSelector
                                            label="Taux de TVA"
                                            value={selectedTax ? {...selectedTax, displayLabel: `${selectedTax.value}% ${selectedTax.name ? `(${selectedTax.name})` : ''}`, hideColor: true} : null}
                                            placeholder="Sélectionner un taux de TVA"
                                            onClick={() => setActivePicker('tax')}
                                            onClear={() => setSelectedTax(null)}
                                        />
                                    </div>
                                    )}

                                    {/* Responsive switches */}
                                    <div className={`grid grid-cols-1 ${isRestaurant && variants.length === 0 ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} gap-3 sm:gap-4 mt-4`}>
                                        <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Activer</span>
                                            <IOSSwitch checked={isActive} onChange={toggleActive}/>
                                        </div>
                                        <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Stockable</span>
                                            <IOSSwitch checked={isStockable} onChange={toggleStockable}/>
                                        </div>
                                        <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Vendable</span>
                                            <IOSSwitch checked={isVendable} onChange={toggleVendable}/>
                                        </div>
                                        {isRestaurant && variants.length === 0 && (
                                            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Ingrédient</span>
                                                <IOSSwitch checked={isIngredient} onChange={toggleIngredient}/>
                                            </div>
                                        )}
                                    </div>

                                    {/* Calendrier : visibilité sur le calendrier / la réservation en ligne + durée (salon uniquement) */}
                                    {isBeautySalon && (
                                    <div className="mt-3 sm:mt-4 bg-gray-50 dark:bg-gray-800 rounded-lg p-3 space-y-3">
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="flex items-start gap-2">
                                                <Calendar className="w-4 h-4 mt-0.5 text-primary flex-shrink-0"/>
                                                <span>
                                                    <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">Afficher dans le calendrier</span>
                                                    <span className="block text-xs text-gray-400 mt-0.5">Proposé au calendrier et à la réservation en ligne</span>
                                                </span>
                                            </span>
                                            <IOSSwitch
                                                checked={showOnCalendar}
                                                onChange={() => setShowOnCalendar(prev => !prev)}
                                            />
                                        </div>

                                        {showOnCalendar && (
                                            <div className="flex items-center gap-2 pl-3 sm:ml-6 border-l-2 border-primary/30">
                                                <Clock className="w-4 h-4 text-gray-400 flex-shrink-0"/>
                                                <label htmlFor="product-duration" className="text-sm text-gray-700 dark:text-gray-300">Durée</label>
                                                <input
                                                    id="product-duration"
                                                    type="number"
                                                    value={duration}
                                                    onChange={(e) => setDuration(parseInt(e.target.value) || 0)}
                                                    min="5"
                                                    step="5"
                                                    className="w-20 px-2.5 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                                                />
                                                <span className="text-sm text-gray-400">minutes</span>
                                            </div>
                                        )}
                                    </div>
                                    )}

                                    {/* Ingrédient — code interne + prix d'achat HT/TTC (restaurant, produit sans variantes) */}
                                    {isRestaurant && variants.length === 0 && isIngredient && (
                                        <div className="pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
                                            <div>
                                                <label htmlFor="internalCodeEdit"
                                                       className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                                                    Code interne :
                                                </label>
                                                <input
                                                    type="text"
                                                    id="internalCodeEdit"
                                                    value={internalCode}
                                                    onChange={(e) => setInternalCode(e.target.value)}
                                                    placeholder="Ex: MOZ-001"
                                                    className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                                />
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                                <div>
                                                    <label htmlFor="purchasePriceHtEdit"
                                                           className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                                                        Prix d'achat HT :
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        id="purchasePriceHtEdit"
                                                        value={purchasePriceHt}
                                                        onChange={(e) => handlePurchaseHtChange(e.target.value)}
                                                        placeholder="Ex: 66.67"
                                                        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                                    />
                                                </div>
                                                <div>
                                                    <label htmlFor="purchasePriceTtcEdit"
                                                           className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                                                        Prix d'achat TTC :
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        id="purchasePriceTtcEdit"
                                                        value={purchasePriceTtc}
                                                        onChange={(e) => handlePurchaseTtcChange(e.target.value)}
                                                        placeholder="Ex: 80.00"
                                                        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                                    />
                                                </div>
                                            </div>
                                            {!selectedTax && (
                                                <p className="text-xs text-orange-500">
                                                    Sélectionnez un taux de TVA ci-dessus pour calculer automatiquement le HT/TTC.
                                                </p>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => setShowPriceHistory(true)}
                                                className="text-sm text-primary hover:underline"
                                            >
                                                Voir l'historique des prix d'achat
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </form>
                        </Tab.Panel>

                        {/* Variants Panel — same list style as Clients / Stock (StockUI) */}
                        {/* Edge-to-edge: cancels the page padding so the list spans the whole screen */}
                        <Tab.Panel className="-mx-3 sm:-mx-4 md:-mx-6 lg:-mx-8 -mt-6 sm:-mt-8">
                            <div>
                                {/* Header with actions — same plain teal buttons as the page header */}
                                <div className="flex items-center justify-between gap-4 px-4 sm:px-8 py-3 border-b border-gray-200 dark:border-gray-700">
                                    <div>
                                        <h2 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100">
                                            Déclinaisons du produit
                                        </h2>
                                        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                            {filteredVariants.length} déclinaison{filteredVariants.length !== 1 ? 's' : ''}
                                            {variants.length !== filteredVariants.length && ` sur ${variants.length}`}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1 sm:gap-2">
                                        <HeaderAction icon={Download} label="Exporter" title="Exporter en CSV" onClick={handleExportVariants}/>
                                        <HeaderAction icon={Copy} label="Création en masse" onClick={() => setShowBulkCreator(true)}/>
                                        <HeaderAction icon={Plus} label="Ajouter" onClick={handleAddVariant}/>
                                    </div>
                                </div>

                                <div>
                                    {/* Toolbar — search + status */}
                                    <div className="flex flex-col sm:flex-row">
                                        <SearchInput value={searchQuery} onChange={setSearchQuery}
                                                     placeholder="Rechercher par nom, code-barres ou prix..."/>
                                        <ToolbarSelect label="Statut" value={filterActive} onChange={setFilterActive}>
                                            <option value="all">Tous les statuts</option>
                                            <option value="active">Actif</option>
                                            <option value="inactive">Inactif</option>
                                        </ToolbarSelect>
                                    </div>

                                    {/* Selection Bar */}
                                    {selectedVariants.length > 0 && (
                                        <div
                                            ref={selectionBarRef}
                                            role="toolbar"
                                            aria-label="Actions sur la sélection"
                                            className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 bg-primary/10 dark:bg-primary/20 border-b border-primary/20 selection-bar-appear"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 bg-primary flex items-center justify-center rounded-full shadow">
                                                    <CheckCircle className="w-4 h-4 text-white"/>
                                                </div>
                                                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                    {selectedVariants.length} déclinaison{selectedVariants.length > 1 ? 's' : ''} sélectionnée{selectedVariants.length > 1 ? 's' : ''}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Button variant="ghost" size="sm" icon={X} compact onClick={() => setSelectedVariants([])}>
                                                    Désélectionner
                                                </Button>
                                                <Button variant="danger" size="sm" icon={Trash2} compact onClick={handleDeleteSelectedVariants}>
                                                    Supprimer
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    <ListCard>
                                        {filteredVariants.length === 0 ? (
                                            <ListEmpty icon={Package} title="Aucune déclinaison trouvée"
                                                       text={searchQuery || filterActive !== 'all'
                                                           ? 'Ajustez la recherche ou les filtres pour retrouver vos déclinaisons.'
                                                           : 'Ajoutez une déclinaison pour commencer.'}/>
                                        ) : (
                                            <>
                                                {/* Desktop Table */}
                                                <div className="hidden lg:block overflow-x-auto">
                                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                                        <thead className="bg-gray-50 dark:bg-gray-700/50">
                                                        <tr>
                                                            <th className="w-12 pl-6 pr-2 py-4 text-left">
                                                                <input
                                                                    type="checkbox"
                                                                    className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                                                    checked={selectedAllVariants}
                                                                    onChange={handleSelectAllVariants}
                                                                />
                                                            </th>
                                                            <VariantSortableHeader field="name" sortConfig={sortConfig} onSort={handleSort}>Déclinaison</VariantSortableHeader>
                                                            <VariantSortableHeader field="price" sortConfig={sortConfig} onSort={handleSort}>Prix</VariantSortableHeader>
                                                            <th className={thClass}>Attributs</th>
                                                            <VariantSortableHeader field="stock" sortConfig={sortConfig} onSort={handleSort}>Stock</VariantSortableHeader>
                                                            <VariantSortableHeader field="active" sortConfig={sortConfig} onSort={handleSort}>Statut</VariantSortableHeader>
                                                            <th className={`${thClass} text-right`}>Actions</th>
                                                        </tr>
                                                        </thead>
                                                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                                        {filteredVariants.map((variant) => (
                                                            <tr
                                                                key={variant.id}
                                                                className={`transition-colors cursor-pointer ${
                                                                    selectedVariants.includes(variant.id)
                                                                        ? 'bg-primary/5 dark:bg-primary/10'
                                                                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                                                }`}
                                                                onClick={() => handleEditVariant(variant.id)}
                                                            >
                                                                <td className="pl-6 pr-2 py-4">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                                                        checked={selectedVariants.includes(variant.id)}
                                                                        onChange={(e) => handleSelectVariant(variant.id, e)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                </td>
                                                                <td className="px-6 py-4">
                                                                    <div className="flex items-center gap-3 min-w-0">
                                                                        <VariantThumb variant={variant} fallbackColor={currentColor}/>
                                                                        <div className="min-w-0">
                                                                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate max-w-xs">
                                                                                {variant.name}
                                                                            </div>
                                                                            {variant.barcode && (
                                                                                <div className="mt-0.5 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                                                                                    <Barcode className="w-3 h-3 flex-shrink-0"/>
                                                                                    <span className="font-mono">{variant.barcode}</span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td className="px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100 tabular-nums whitespace-nowrap">
                                                                    {formatVariantPrice(variant.price)}
                                                                </td>
                                                                <td className="px-6 py-4 text-sm">
                                                                    <VariantAttributes variant={variant} empty={<span className="text-gray-400">—</span>}/>
                                                                </td>
                                                                <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 tabular-nums">
                                                                    {variant.stock !== undefined ? variant.stock : '—'}
                                                                </td>
                                                                <td className="px-6 py-4">
                                                                    <VariantStatus active={variant.active}/>
                                                                </td>
                                                                <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                                                                    <VariantActions
                                                                        variant={variant}
                                                                        onEdit={handleEditVariant}
                                                                        onDuplicate={handleDuplicateVariant}
                                                                        onDelete={handleDeleteVariant}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        ))}
                                                        </tbody>
                                                    </table>
                                                </div>

                                                {/* Mobile rows — edge to edge, same as the Stock / catalogue lists */}
                                                <div className={mobileListClass}>
                                                    {filteredVariants.map((variant) => (
                                                        <div
                                                            key={variant.id}
                                                            className={`${mobileRowClass} cursor-pointer ${selectedVariants.includes(variant.id) ? '!bg-primary/5 dark:!bg-primary/10' : ''}`}
                                                            onClick={() => handleEditVariant(variant.id)}
                                                        >
                                                            <div className="flex items-start gap-3">
                                                                <input
                                                                    type="checkbox"
                                                                    className="mt-1 w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                                                    checked={selectedVariants.includes(variant.id)}
                                                                    onChange={(e) => handleSelectVariant(variant.id, e)}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                />
                                                                <VariantThumb variant={variant} fallbackColor={currentColor} size="lg"/>
                                                                <div className="flex-1 min-w-0">
                                                                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                                                                        {variant.name}
                                                                    </h3>
                                                                    {variant.barcode && (
                                                                        <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                                                                            <Barcode className="w-3 h-3 flex-shrink-0"/>
                                                                            <span className="font-mono truncate">{variant.barcode}</span>
                                                                        </p>
                                                                    )}
                                                                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                                                                        <VariantStatus active={variant.active}/>
                                                                        <VariantAttributes variant={variant}/>
                                                                    </div>
                                                                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                                                        <span className="font-semibold text-gray-900 dark:text-gray-100">{formatVariantPrice(variant.price)}</span>
                                                                        {variant.stock !== undefined && ` · Stock : ${variant.stock}`}
                                                                    </p>
                                                                </div>
                                                                <div onClick={(e) => e.stopPropagation()}>
                                                                    <VariantActions
                                                                        variant={variant}
                                                                        onEdit={handleEditVariant}
                                                                        onDuplicate={handleDuplicateVariant}
                                                                        onDelete={handleDeleteVariant}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </ListCard>
                                </div>
                            </div>
                        </Tab.Panel>

                        {/* Options Panel: "Options" (Restaurant) / "Packs" (other commerce types) */}
                        <Tab.Panel>
                            <ProductOptionGroups
                                productId={productId}
                                groupLabel={isRestaurant ? "groupe" : "pack"}
                                onNotify={showNotification}
                            />
                        </Tab.Panel>
                    </Tab.Panels>
                </Tab.Group>
            </div>

            {/* Color palette overlay - responsive & scrollable */}
            {showPalette && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={togglePalette}/>
                    <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
                        <div className="relative w-full sm:max-w-2xl bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col h-[85vh] sm:h-auto sm:max-h-[85vh]"
                             onClick={e => e.stopPropagation()}>
                            <ColorPalette
                                initialColor={currentColor}
                                onClose={togglePalette}
                                onColorChange={handleColorChange}
                                onImageChange={handleImageChange}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Responsive Confirmation Dialog */}
            {showConfirmDialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-4 sm:p-6 max-w-md w-full selection-bar-appear">
                        <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                            Confirmation de suppression
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                            Êtes-vous sûr de vouloir supprimer {variantsToDelete.length} déclinaison{variantsToDelete.length > 1 ? 's' : ''} ?
                            Cette action est irréversible.
                        </p>
                        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
                            <Button onClick={cancelDelete}>Annuler</Button>
                            <Button variant="dangerSolid" icon={Trash2} onClick={confirmDelete}>Supprimer</Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Responsive Notification Modal */}
            <Notification
                show={notification.show}
                type={notification.type}
                message={notification.message}
                onClose={hideNotification}
            />

            {/* Add required CSS for animations */}
            <style jsx>{`
                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .selection-bar-appear {
                    animation: slideDown 0.3s ease-out forwards;
                }

                @keyframes scaleIn {
                    from {
                        opacity: 0;
                        transform: scale(.95);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }

                .card-appear {
                    animation: scaleIn 0.2s ease-out forwards;
                }
            `}</style>

            {/* Bulk Variant Creator Modal */}
            {showBulkCreator && (
                <BulkVariantCreator
                    productId={productId}
                    onClose={() => setShowBulkCreator(false)}
                    onSuccess={() => {
                        // Reload product data to get new variants
                        fetchProduct();
                    }}
                />
            )}

            {/* Reference Picker Modals */}
            {activePicker === 'category' && (
                <ReferencePickerModal
                    config={categoriesConfig}
                    currentValue={selectedCategory?.id}
                    onSelect={(item) => { setSelectedCategory(item); setValue("categoryId", item.id); setActivePicker(null); }}
                    onClose={() => setActivePicker(null)}
                />
            )}
            {activePicker === 'brand' && (
                <ReferencePickerModal
                    config={brandsConfig}
                    currentValue={selectedBrand?.id}
                    onSelect={(item) => { setSelectedBrand(item); setValue("brandId", item.id); setActivePicker(null); }}
                    onClose={() => setActivePicker(null)}
                />
            )}
            {activePicker === 'unit' && (
                <ReferencePickerModal
                    config={unitsConfig}
                    currentValue={selectedUnit?.id}
                    onSelect={(item) => { setSelectedUnit(item); setValue("unitsId", item.id); setActivePicker(null); }}
                    onClose={() => setActivePicker(null)}
                    displayValue={(item) => `${item.name} (${item.code || ''})`}
                />
            )}
            {activePicker === 'tax' && (
                <ReferencePickerModal
                    config={taxesConfig}
                    currentValue={selectedTax?.id}
                    onSelect={(item) => { setSelectedTax(item); setValue("taxId", item.id); setActivePicker(null); }}
                    onClose={() => setActivePicker(null)}
                    displayValue={(item) => `${item.value}% ${item.name ? `(${item.name})` : ''}`}
                />
            )}

            {/* Convert-to-variant: target product picker */}
            {showConvertPicker && (
                <ReferencePickerModal
                    config={productsConfig}
                    onSelect={handleSelectConvertTarget}
                    onClose={() => setShowConvertPicker(false)}
                />
            )}

            {/* Convert-to-variant: confirmation dialog */}
            {convertTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-4 sm:p-6 max-w-md w-full selection-bar-appear">
                        <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                            Convertir en déclinaison
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                            Ce produit va devenir une déclinaison de <strong className="text-gray-800 dark:text-gray-200">{convertTarget.name}</strong>.
                            Le produit actuel sera supprimé. Cette action est irréversible.
                        </p>
                        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
                            <Button onClick={cancelConvert} disabled={isConverting}>Annuler</Button>
                            <Button variant="warningSolid" icon={GitMerge} loading={isConverting} onClick={confirmConvert}>
                                {isConverting ? 'Conversion…' : 'Convertir'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {showPriceHistory && (
                <PurchasePriceHistoryModal
                    productId={productId}
                    productName={watch("name") || ""}
                    onClose={() => setShowPriceHistory(false)}
                />
            )}
        </div>
    );
};

export default EditProductForm;
